import {useEffect, useState} from 'react';
import {ReactDiagram} from 'gojs-react';
import {FaExternalLinkAlt} from 'react-icons/fa';

import {FlatGraph} from 'src/state/dependency2';
import go, {Diagram, Node} from 'src/util/gojs/go';
import {toArray, isSearchNode, isGroupNode} from 'src/util/gojs';
import {INVESTIGATE_USAGES_PATH} from 'src/routes';
import {useContextMenuPosition} from 'src/hooks';
import {
  Modal,
  NodeDetailModal,
  ContextMenu,
  MenuItem,
} from 'src/components';
import {fetchDataForNode} from 'src/graph';
import {commitNodesToDiagram} from 'src/graph';
import {RelationshipDetailModal} from 'src/components/RelationshipDetail';

import {useSelectList} from '../GraphContext';

import {GraphViewNodeFilter} from './components';
import {NodeData} from './types';
import {
  hideChildren,
  initDiagram,
  toggleNodeExpansion,
} from './utils';

import '../dependency.css';

export {initDiagram};

export function GraphView({
  flatGraph,
  reset,
  toggleReset,
  impact,
  setCurrNode,
  forceLoad = 0,
  analyzeImpact,
  onNodesAdded,
  onLinksAdded,
}: {
  flatGraph: FlatGraph,
  reset: boolean,
  toggleReset: () => void,
  impact: boolean,
  setCurrNode: (data: any) => void
  forceLoad?: number,
  analyzeImpact?: (node) => void;
  onNodesAdded?: (nodes: Array<go.ObjectData>) => void;
  onLinksAdded?: (links: Array<go.ObjectData>) => void;
}) {
  const navigateToInvestigateUsages = (nodeId: string) => window.open(INVESTIGATE_USAGES_PATH.replace('/:id/', `/${nodeId}/`), '_blank');

  const {
    contextMenuState,
    showContextMenu,
    hideContextMenu,
  } = useContextMenuPosition();

  const [graphRenderData, setGraphRenderData] = useState<any>({
    nodes: [],
    edges: [],
  });
  const [diagram, setDiagram] = useState<Diagram>();
  const [skipModelUpdate, setSkipModelUpdate] = useState(false);

  const [isShowContextMenuParent, setIsShowContextMenuParent] = useState(false);
  const [isShowContextModal, setIsShowContextModal] = useState(false);

  const showContextModal = () => setIsShowContextModal(true);
  const hideContextModal = () => setIsShowContextModal(false);
  const {selectDispatch} = useSelectList();
  const [isNodeDetailOpen, setNodeDetailOpen] = useState(false);
  const [nodeDetailId, setNodeDetailId] = useState<string>();
  const [isLinkDetailOpen, setLinkDetailOpen] = useState(false);

  const updateFilteredNodes = (
      selectedNodes: Array<NodeData>,
      unselectedNodes: Array<NodeData>,
  ) => {
    if (diagram) {
      const node = diagram.findNodeForKey(contextNodeKey);
      if (node) {
        // need to make sure the parent is shown in case it was previously hidden
        flatGraph.showNode('' + node.key);
        selectedNodes.forEach((n) => {
          flatGraph.showNode(n.id);
        });

        commitNodesToDiagram(diagram, {nodes: selectedNodes, edges: flatGraph.edges} ?? []);
        unselectedNodes.forEach(({id}) => flatGraph.hideNode(id));
        unselectedNodes.forEach((node) => {
          const goNode = diagram.findNodeForKey(node.id);
          if (goNode) diagram.remove(goNode);
        });

        flatGraph.nodes = selectedNodes;

        if (unselectedNodes.length > 0 && selectedNodes.length === 0) {
          // hide all nodes
          node.collapseTree();
          node.data.wasLoaded = false;
          hideContextModal();
        } else if (selectedNodes.length > 0 && unselectedNodes.length === 0) {
          // show all nodes
          node.expandTree();
          node.data.wasLoaded = true;
          hideContextModal();
        } else {
          // a mix of show and hide
          node.expandTree();
          unselectedNodes.forEach((node) => {
            const goNode = diagram.findNodeForKey(node.id);
            if (goNode) diagram.remove(goNode);
          });
          node.data.wasLoaded = true;
          hideContextModal();
        }
      }
    }
  };

  const [contextNodeKey, setContextNodeKey] = useState<string | number>();
  const contextNode = (contextNodeKey && diagram) ? diagram.findNodeForKey(contextNodeKey) : null;
  const [contextLinkKey, setContextLinkKey] = useState<string | number>();
  const contextLink = (contextLinkKey && diagram) ? diagram.findLinkForKey(contextLinkKey) : null;
  const [contextNodeChildren, setContextNodeChildren] = useState<Array<any>>([]);

  const fetchNodeChildren = async (node: Node) => {
    const {nodes: newNodes, edges} = await fetchDataForNode(node);
    const knownNodes = toArray(node.findNodesOutOf() ?? []);

    const finalList = [
      ...knownNodes.map((n) => n.data).map((data) => ({
        ...data, selected: flatGraph.isNodeTracked(data.id) ? flatGraph.isNodeVisible(data.id) : undefined,
      })),
      ...newNodes.map((n) => ({
        ...n, selected: flatGraph.isNodeTracked(n.id) ? flatGraph.isNodeVisible(n.id) : undefined,
      })),
    ];

    setContextNodeChildren(finalList);
    selectDispatch({type: 'updateSelectList', payload: {list: finalList, hiddenMap: flatGraph.hiddenNodesMap}});
    flatGraph.edges = edges;
  };

  useEffect(() => {
    if (contextNode && !isSearchNode(contextNode.data)) {
      fetchNodeChildren(contextNode);
    }
  }, [contextNode, isShowContextModal]);

  async function showNode() {
    const diagram = go.Diagram.fromDiv(document.getElementsByClassName('diagram-component')[0]);
    if (!diagram) {
      return;
    }
    const node = diagram.findNodeForKey(contextNodeKey);
    if (!node) {
      return;
    }
    diagram.startTransaction('showNode');
    node.data.wasLoaded = false;
    await toggleNodeExpansion(
        node,
        flatGraph,
        setCurrNode,
        setSkipModelUpdate,
        handleSelects,
    );
    diagram.commitTransaction('showNode');
    hideContextMenu();
  }

  function hideNode() {
    const diagram = go.Diagram.fromDiv(document.getElementsByClassName('diagram-component')[0]);
    if (!diagram) {
      return;
    }
    const node = diagram.findNodeForKey(contextNodeKey);
    if (!node) {
      return;
    }

    // needed in case children are showing when parent is hidden
    const childNodes = node.findTreeChildrenNodes();
    for (let it = childNodes; it.next(); ) {
      const n = it.value;
      flatGraph.hideNode('' + n.key);
    }

    diagram.startTransaction('hideNode');

    flatGraph.hideNode('' + node.key);
    hideChildren(node);
    diagram.commitTransaction('hideNode');
    hideContextMenu();
  }


  const handleSelects = (method: string, data: any) => {
    switch (method) {
      case 'update':
        selectDispatch({type: 'updateSelectList', payload: data});
        break;
      case 'collapse':
        selectDispatch({type: 'removeSelectList', payload: data});
        break;
      case 'expand':
        selectDispatch({type: 'expandSelectList', payload: data});
        break;
      default:
    }
  };

  useEffect(() => {
    const waitForLoad = () => {
      if (reset) {
        diagram?.clear();
        setSkipModelUpdate(false);
        setGraphRenderData({
          nodes: flatGraph.nodes ?? [],
          edges: flatGraph.edges ?? [],
        });
        toggleReset();
        diagram?.nodes?.each((it) => {
          it.data.wasLoaded = false;
          if (impact) {
            it.isTreeExpanded = true;
          } else {
            it.isTreeExpanded = false;
          }
        });
      }
    };

    if (reset) {
      waitForLoad();
    }
  }, [reset, impact, forceLoad, flatGraph]);

  const goToNode = (id, name) => {
    setNodeDetailId(id);
  };

  const showNodeDetails = () => {
    const diagram = go.Diagram.fromDiv(document.getElementsByClassName('diagram-component')[0]);
    if (!diagram) {
      return;
    }
    const node = diagram.findNodeForKey(contextNodeKey);
    if (!node) {
      return;
    }

    setNodeDetailId('' + node.key);
    setNodeDetailOpen(true);
  };

  const getImpactAnalysis = () => {
    const diagram = go.Diagram.fromDiv(document.getElementsByClassName('diagram-component')[0]);
    if (!diagram) {
      return;
    }
    const node = diagram.findNodeForKey(contextNodeKey);
    if (!node) {
      return;
    }
    if (analyzeImpact) {
      analyzeImpact(node);
    }
  };

  const exportDiagramAsSVG = () => {
    const svg = diagram?.makeSvg(
        {
          scale: 1,
          maxSize: new go.Size(Infinity, Infinity),
        },
    );
    const rootNode = svg?.getRootNode({});
    if (rootNode) {
      const svgstr = new XMLSerializer().serializeToString(rootNode);
      const blob = new Blob([svgstr], {type: 'image/svg+xml'});

      const url = window.URL.createObjectURL(blob);
      const filename = 'mySVGFile.svg';

      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      requestAnimationFrame(() => {
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      });
    }
  };

  if (graphRenderData.nodes.length > 0) {
    return (
      <>
        <ReactDiagram
          initDiagram={() => {
            const diagram = initDiagram({
              flatGraph,
              setIsShowContextMenuParent,
              showContextMenu,
              setContextNodeKey,
              setSkipModelUpdate,
              setCurrNode,
              handleSelects,
              setContextLinkKey,
            });
            setDiagram(diagram);
            return diagram;
          }}
          divClassName="diagram-component"
          nodeDataArray={graphRenderData.nodes}
          linkDataArray={graphRenderData.edges}
          skipsDiagramUpdate={skipModelUpdate}
          onModelChange={(e) => {
            if (e.modifiedNodeData && onNodesAdded) {
              onNodesAdded(e.modifiedNodeData);
            }
            if (e.modifiedLinkData && onLinksAdded) {
              onLinksAdded(e.modifiedLinkData);
            }
          }}
        />
        <ContextMenu {...contextMenuState}>
          {contextNode && contextNode.data && <MenuItem onClick={()=>showNodeDetails()} >Item Details</MenuItem>}
          {contextNode && isShowContextMenuParent && <MenuItem onClick={showContextModal} isSection={true}>Show/hide child item</MenuItem>}
          {contextNode && <MenuItem onClick={hideNode}>Hide</MenuItem>}
          {contextNode && isShowContextMenuParent && <MenuItem onClick={showNode} >Show</MenuItem>}
          {contextNode && contextNode.data && !isSearchNode(contextNode.data) && !isGroupNode(contextNode.data) && (
            <MenuItem
              onClick={() => contextNodeKey && navigateToInvestigateUsages('' + contextNodeKey)} isSection={true}
            >
              Investigate Usages <FaExternalLinkAlt fontSize="0.625rem" />
            </MenuItem>
          )}
          {contextNode && contextNode.data && !isGroupNode(contextNode.data) && <MenuItem onClick={getImpactAnalysis} >Impact Analysis</MenuItem>}
          {contextLink &&
            contextLink.data &&
            contextLink.data.to &&
            contextLink.data.from &&
            contextLink.data.type &&
            <MenuItem onClick={() => {
              setLinkDetailOpen(true);
            }}>Relationship Details</MenuItem>}
          {!contextNode && !contextLink &&
            <MenuItem onClick={exportDiagramAsSVG}>Export SVG</MenuItem>
          }
        </ContextMenu>
        {diagram && contextNode &&
          <Modal className="" placement="right" toggle={hideContextModal} isOpen={isShowContextModal}>
            <GraphViewNodeFilter
              node={contextNode}
              nodes={contextNodeChildren}
              diagram={diagram}
              handleCancel={hideContextModal}
              handleUpdate={updateFilteredNodes}
              handleSelects={handleSelects}
            />
          </Modal>}
        {isNodeDetailOpen && (
          <NodeDetailModal
            isOpen={isNodeDetailOpen}
            toggle={() => setNodeDetailOpen(false)}
            nodeId={nodeDetailId}
            goToNode={goToNode}
          />
        )}
        {isLinkDetailOpen &&
          contextLink &&
          contextLink.data &&
          contextLink.data.to &&
          contextLink.data.from &&
          contextLink.data.type && (
          <RelationshipDetailModal
            isOpen={isLinkDetailOpen}
            toggle={() => setLinkDetailOpen(false)}
            to={contextLink.data.to}
            from={contextLink.data.from}
            type={contextLink.data.type}
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
}
